import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import type { Line } from '~/api/common/types';
import type { UserLanguage } from '~/api/user/types';

import { createAppColumnHelper } from '~/components';
import { ColumnIds } from '~/constants/table';
import { calculateLineDiscount, DocumentLineDescriptionCell } from '~/features/documents';
import { useIntl } from '~/hooks/useIntl';

import { QuotationLineMobileCell } from './QuotationLineMobileCell';
import { useFormatQuantity } from './useFormatQuantity';

export type QuotationLineRow = {
  id: number;
  line: Line;
};

const columnHelper = createAppColumnHelper<QuotationLineRow>();

export const useQuotationLinesTableColumns = ({ language }: { language: UserLanguage }) => {
  const { formatCurrency, formatPercentage } = useIntl(language);
  const formatQuantity = useFormatQuantity(language);
  const { t } = useTranslation(['lines']);

  return useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          id: ColumnIds.documentLinesDescription,
          header: t('lines:description.header'),
          cell: ({ row: { original } }) => <DocumentLineDescriptionCell line={original.line} />,
          enableSorting: false,
          meta: { styles: { size: '1fr', minSize: 200 } },
        }),

        columnHelper.display({
          id: ColumnIds.documentLinesQuantity,
          header: t('lines:quantity.header'),
          cell: ({ row: { original } }) => (original.line.type === 'entry' ? formatQuantity(original.line) : null),
          enableSorting: false,
          meta: { styles: { size: 'max-content', minSize: 80, justify: 'end' } },
        }),

        columnHelper.display({
          id: ColumnIds.documentLinesPrice,
          header: t('lines:price.header'),
          cell: ({ row: { original } }) => (original.line.type === 'entry' ? formatCurrency(original.line.price) : null),
          enableSorting: false,
          meta: { styles: { size: 'max-content', minSize: 100, justify: 'end' } },
        }),

        columnHelper.display({
          id: ColumnIds.documentLinesDiscount,
          header: t('lines:discount.header'),
          cell: ({
            row: {
              original: { line },
            },
          }) =>
            line.type === 'entry' && line.discountType !== null
              ? match(line.discountType)
                  .with('flatRate', () => formatCurrency(line.discountValue ?? 0))
                  .with('percentage', () => formatPercentage((line.discountValue ?? 0) / 100))
                  .exhaustive()
              : null,
          enableSorting: false,
          meta: { styles: { size: 'max-content', minSize: 90, justify: 'end' } },
        }),

        columnHelper.display({
          id: ColumnIds.documentLinesTotal,
          header: t('lines:lineTotal.header'),
          cell: ({ row: { original } }) =>
            original.line.type === 'entry'
              ? formatCurrency(original.line.quantity * original.line.price - calculateLineDiscount(original.line))
              : null,
          enableSorting: false,
          meta: { styles: { size: 'max-content', minSize: 100, justify: 'end' } },
        }),

        columnHelper.display({
          id: ColumnIds.documentLinesMobile,
          cell: ({ row: { original } }) => <QuotationLineMobileCell language={language} line={original.line} />,
          enableSorting: false,
          meta: { mobile: true },
        }),
      ]),
    [formatCurrency, formatPercentage, formatQuantity, language, t],
  );
};
